import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  UserCircle, Handshake, MagnifyingGlass, Plus, MapPin, Envelope,
  Phone, Trash, PencilSimple, X, Image, ArrowRight
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ListingCard({ listing, onDelete, isOwner }) {
  const isAgent = listing.listing_type === 'agent';

  return (
    <div className="bg-white border-2 border-[#004E89] rounded-2xl shadow-[4px_4px_0px_#004E89] hover:-translate-y-1 transition-transform overflow-hidden" data-testid={`service-listing-${listing.id}`}>
      <div className={`px-4 py-2 border-b-2 border-[#004E89] ${isAgent ? 'bg-[#004E89]' : 'bg-[#FF6B35]'}`}>
        <div className="flex items-center justify-between">
          <span className="text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
            {isAgent ? <><Handshake size={14} weight="bold" /> Estate Sale Agent</> : <><UserCircle size={14} weight="bold" /> Looking for Help</>}
          </span>
          {isOwner && (
            <button onClick={() => onDelete(listing.id, listing.name)} className="text-white/70 hover:text-white transition-colors" data-testid={`delete-listing-${listing.id}`}>
              <Trash size={14} weight="bold" />
            </button>
          )}
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-start gap-4">
          {listing.photo ? (
            <img src={`${API}/files/${listing.photo}`} alt={listing.name}
              className="w-16 h-16 rounded-xl border-2 border-[#004E89] object-cover flex-shrink-0" />
          ) : (
            <div className="w-16 h-16 rounded-xl border-2 border-[#004E89] bg-[#FFD166] flex items-center justify-center flex-shrink-0">
              <UserCircle size={32} weight="duotone" className="text-[#004E89]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-[#004E89] truncate" style={{ fontFamily: 'Outfit' }}>{listing.name}</h3>
            <div className="flex items-center gap-1.5 text-[#5C7A99] text-sm font-medium mt-0.5">
              <MapPin size={14} weight="bold" className="text-[#FF6B35] flex-shrink-0" />
              <span className="truncate">{listing.service_area}</span>
            </div>
          </div>
        </div>

        <p className="text-[#5C7A99] text-sm leading-relaxed mt-4 line-clamp-3">{listing.bio}</p>

        <div className="mt-4 flex flex-wrap gap-2">
          {listing.contact_email && (
            <a href={`mailto:${listing.contact_email}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#004E89] bg-[#004E89]/5 border border-[#004E89]/20 rounded-lg px-3 py-1.5 hover:bg-[#004E89]/10 transition-colors"
              data-testid={`contact-email-${listing.id}`}>
              <Envelope size={12} weight="bold" /> {listing.contact_email}
            </a>
          )}
          {listing.contact_phone && (
            <a href={`tel:${listing.contact_phone}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#004E89] bg-[#004E89]/5 border border-[#004E89]/20 rounded-lg px-3 py-1.5 hover:bg-[#004E89]/10 transition-colors"
              data-testid={`contact-phone-${listing.id}`}>
              <Phone size={12} weight="bold" /> {listing.contact_phone}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateListingModal({ type, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [serviceArea, setServiceArea] = useState('');
  const [photo, setPhoto] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isAgent = type === 'agent';

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload`, formData, { withCredentials: true });
      setPhoto(data.path);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !bio.trim() || !serviceArea.trim()) {
      toast.error('Please fill in name, description, and service area');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/service-listings`, {
        listing_type: type,
        name: name.trim(),
        bio: bio.trim(),
        contact_email: contactEmail.trim(),
        contact_phone: contactPhone.trim(),
        service_area: serviceArea.trim(),
        photo,
      }, { withCredentials: true });
      toast.success(isAgent ? 'Your agent profile is now live!' : 'Your request has been posted!');
      onCreated(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create listing');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-[#004E89] rounded-2xl shadow-[8px_8px_0px_#004E89] max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-listing-modal">
        <div className={`px-6 py-4 border-b-2 border-[#004E89] flex items-center justify-between ${isAgent ? 'bg-[#004E89]' : 'bg-[#FF6B35]'}`}>
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>
            {isAgent ? 'Offer Your Services' : 'Post a Help Request'}
          </h2>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors" data-testid="close-modal-btn">
            <X size={20} weight="bold" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-sm font-bold text-[#004E89] block mb-1">
              {isAgent ? 'Your Name / Business Name' : 'Your Name'}
            </label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} required
              placeholder={isAgent ? 'e.g. Smith Estate Services' : 'e.g. John Doe'}
              className="w-full border-2 border-[#004E89] rounded-xl p-3 text-sm font-medium text-[#004E89] focus:ring-2 focus:ring-[#FFD166] focus:outline-none"
              data-testid="listing-name-input" />
          </div>
          <div>
            <label className="text-sm font-bold text-[#004E89] block mb-1">
              {isAgent ? 'About Your Services' : 'What do you need help with?'}
            </label>
            <textarea value={bio} onChange={e => setBio(e.target.value)} required rows={4}
              placeholder={isAgent ? 'Describe your experience, services offered, and what sets you apart...' : 'Describe the estate sale you need help running, size of the estate, timeline, etc.'}
              className="w-full border-2 border-[#004E89] rounded-xl p-3 text-sm font-medium text-[#004E89] focus:ring-2 focus:ring-[#FFD166] focus:outline-none resize-none"
              data-testid="listing-bio-input" />
          </div>
          <div>
            <label className="text-sm font-bold text-[#004E89] block mb-1">Service Area / Location</label>
            <input type="text" value={serviceArea} onChange={e => setServiceArea(e.target.value)} required
              placeholder="e.g. Tampa, FL or Tri-State Area"
              className="w-full border-2 border-[#004E89] rounded-xl p-3 text-sm font-medium text-[#004E89] focus:ring-2 focus:ring-[#FFD166] focus:outline-none"
              data-testid="listing-area-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-bold text-[#004E89] block mb-1">Email</label>
              <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full border-2 border-[#004E89] rounded-xl p-3 text-sm font-medium text-[#004E89] focus:ring-2 focus:ring-[#FFD166] focus:outline-none"
                data-testid="listing-email-input" />
            </div>
            <div>
              <label className="text-sm font-bold text-[#004E89] block mb-1">Phone</label>
              <input type="tel" value={contactPhone} onChange={e => setContactPhone(e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full border-2 border-[#004E89] rounded-xl p-3 text-sm font-medium text-[#004E89] focus:ring-2 focus:ring-[#FFD166] focus:outline-none"
                data-testid="listing-phone-input" />
            </div>
          </div>
          <div>
            <label className="text-sm font-bold text-[#004E89] block mb-1">Profile Photo (optional)</label>
            <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-[#004E89]/40 rounded-xl p-3 hover:border-[#004E89] transition-colors">
              <Image size={20} className="text-[#5C7A99]" />
              <span className="text-sm text-[#5C7A99] font-medium">
                {uploading ? 'Uploading...' : photo ? 'Photo uploaded' : 'Click to upload'}
              </span>
              <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="listing-photo-input" />
            </label>
          </div>
          <button type="submit" disabled={submitting}
            className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="listing-submit-btn">
            {submitting ? 'Posting...' : isAgent ? 'List My Services' : 'Post My Request'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function HireAgentPage() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('agents');
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(null); // null | 'agent' | 'request'
  const limit = 12;

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, listing_type: activeTab === 'agents' ? 'agent' : 'request' };
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/service-listings`, { params });
      setListings(data.listings);
      setTotal(data.total);
    } catch (err) {
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  }, [page, search, activeTab]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete listing "${name}"?`)) return;
    try {
      await axios.delete(`${API}/service-listings/${id}`, { withCredentials: true });
      toast.success('Listing deleted');
      fetchListings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-[#F9F9F6] pt-20 pb-16" data-testid="hire-agent-page">
      {/* Hero Banner */}
      <section className="bg-[#004E89] border-b-2 border-[#004E89] px-4 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit' }} data-testid="hire-page-title">
            Need Someone to Run <span className="text-[#FFD166]">Your Sale?</span>
          </h1>
          <p className="text-[#B8D4E8] text-base sm:text-lg max-w-2xl mx-auto mb-8">
            Connect with experienced estate sale professionals or post a request and let agents come to you.
          </p>
          {user && (
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={() => setShowCreateModal('agent')}
                className="btn-secondary py-3 px-6 text-sm flex items-center gap-2"
                data-testid="offer-services-btn">
                <Handshake size={18} weight="bold" /> Offer My Services
              </button>
              <button onClick={() => setShowCreateModal('request')}
                className="bg-white text-[#004E89] border-2 border-white rounded-xl font-bold shadow-[4px_4px_0px_rgba(0,0,0,0.2)] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_rgba(0,0,0,0.2)] transition-all py-3 px-6 text-sm flex items-center gap-2"
                data-testid="post-request-btn">
                <UserCircle size={18} weight="bold" /> I Need Help
              </button>
            </div>
          )}
          {!user && (
            <Link to="/register" data-testid="hire-signup-cta">
              <button className="btn-secondary py-3 px-8 text-base flex items-center gap-2 mx-auto">
                Sign Up to Get Started <ArrowRight size={18} weight="bold" />
              </button>
            </Link>
          )}
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); setPage(1); }} className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <TabsList className="bg-white border-2 border-[#004E89] rounded-2xl p-1.5 shadow-[4px_4px_0px_#004E89] flex gap-1" data-testid="hire-tabs">
              <TabsTrigger value="agents" className="rounded-xl px-5 py-2.5 text-sm font-bold data-[state=active]:bg-[#004E89] data-[state=active]:text-white text-[#004E89]" data-testid="hire-tab-agents">
                <Handshake size={16} weight="bold" className="mr-1.5" /> Estate Sale Agents
              </TabsTrigger>
              <TabsTrigger value="requests" className="rounded-xl px-5 py-2.5 text-sm font-bold data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-[#004E89]" data-testid="hire-tab-requests">
                <UserCircle size={16} weight="bold" className="mr-1.5" /> Help Wanted
              </TabsTrigger>
            </TabsList>
            <div className="flex-1 relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C7A99]" />
              <input type="text" placeholder="Search by name, area, or keywords..." value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-[#004E89] rounded-xl text-sm font-medium text-[#004E89] focus:ring-2 focus:ring-[#FFD166] focus:outline-none"
                data-testid="hire-search-input" />
            </div>
          </div>

          <TabsContent value="agents">
            {loading ? (
              <div className="text-center py-16 text-[#5C7A99] font-bold">Loading agents...</div>
            ) : listings.length === 0 ? (
              <div className="text-center py-16">
                <Handshake size={48} weight="duotone" className="text-[#5C7A99]/40 mx-auto mb-4" />
                <p className="text-[#5C7A99] font-bold text-lg mb-2">No agents listed yet</p>
                <p className="text-[#5C7A99] text-sm mb-6">Be the first to offer your estate sale services!</p>
                {user && (
                  <button onClick={() => setShowCreateModal('agent')} className="btn-primary py-2.5 px-6 text-sm flex items-center gap-2 mx-auto" data-testid="empty-offer-btn">
                    <Plus size={16} weight="bold" /> Offer My Services
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agents-grid">
                {listings.map(l => (
                  <ListingCard key={l.id} listing={l} onDelete={handleDelete} isOwner={user && user._id === l.created_by} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="requests">
            {loading ? (
              <div className="text-center py-16 text-[#5C7A99] font-bold">Loading requests...</div>
            ) : listings.length === 0 ? (
              <div className="text-center py-16">
                <UserCircle size={48} weight="duotone" className="text-[#5C7A99]/40 mx-auto mb-4" />
                <p className="text-[#5C7A99] font-bold text-lg mb-2">No help requests posted yet</p>
                <p className="text-[#5C7A99] text-sm mb-6">Looking for someone to run your estate sale? Post a request!</p>
                {user && (
                  <button onClick={() => setShowCreateModal('request')} className="btn-primary py-2.5 px-6 text-sm flex items-center gap-2 mx-auto" data-testid="empty-request-btn">
                    <Plus size={16} weight="bold" /> Post a Request
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="requests-grid">
                {listings.map(l => (
                  <ListingCard key={l.id} listing={l} onDelete={handleDelete} isOwner={user && user._id === l.created_by} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-8">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="px-4 py-2 border-2 border-[#004E89] rounded-xl text-sm font-bold text-[#004E89] disabled:opacity-30 hover:bg-[#FFD166] transition-colors" data-testid="hire-prev-page">
              Previous
            </button>
            <span className="text-sm font-bold text-[#004E89]">{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="px-4 py-2 border-2 border-[#004E89] rounded-xl text-sm font-bold text-[#004E89] disabled:opacity-30 hover:bg-[#FFD166] transition-colors" data-testid="hire-next-page">
              Next
            </button>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateListingModal type={showCreateModal} onClose={() => setShowCreateModal(null)} onCreated={() => fetchListings()} />
      )}
    </div>
  );
}
